import { getNestedOrFloatingElementById, getPersonIdsForField } from '@fs/zion-sls'

/**
 * Get the person data for the persons sharing this element. This is a simpler variation of a function found in other panels.
 * It assumes the element exists and is not being created.
 * @param {String} selectedPersonId - id of the selected person
 * @param {Array} images - zionSlsState.images
 * @param {Object} element - SLS element to get the attached persons from
 * @returns {Array} Persons sharing the element
 */
export const getPersonsFromElement = ({ selectedPersonId, images, element }) => {
  const elementCopy = { ...element }
  const personIds = getPersonIdsForField(elementCopy, images)
  const persons = personIds.map((personId) => getNestedOrFloatingElementById(images, personId))
  persons.sort((a, b) => {
    if (a.id === selectedPersonId) return -1
    if (b.id === selectedPersonId) return 1
    return 0
  })

  return persons.flat() || []
}

/**
 * Sort person ids by ark. Persons sharing the element will be added to the selected and display arrays. Persons in the current record will be added to the display array.
 * We want dangling persons from the record to be displayed in the other arks section, even if they are not attached.
 * @param {Array} allPersonsSharingElement - persons who share the element
 * @param {Array} personsInThisRecord - persons in the current record. They will be added to the display array for the appropriate ark.
 * @returns {*} associative array with objects for each ark with selected and display arrays
 */
export const getSelectedAndDisplayPersonsByArk = ({ allPersonsSharingElement, personsInThisRecord }) => {
  const addPersonOrArray = (acc, person) => {
    if (!acc[person.partitionKey]) {
      acc[person.partitionKey] = { selected: [person], display: [person] }
    } else {
      acc[person.partitionKey].selected.push(person)
      acc[person.partitionKey].display.push(person)
    }
    return acc
  }

  const result = allPersonsSharingElement?.reduce(addPersonOrArray, {}) || {}
  /* e.g. { '3:1:etc': [...personsOnThisImage] }, '3:1:etc': [...personsOnOtherImage] } */
  personsInThisRecord.forEach((person) => {
    if (!result[person.partitionKey]) {
      result[person.partitionKey] = { selected: [], display: [person] }
    } else if (!result[person.partitionKey].display.includes(person)) {
      result[person.partitionKey].display.push(person)
    }
  })
  return result
}

/**
 * Helper function to set selection values for both record and image
 * @param {Object} params - Parameters object
 * @param {Array} params.personIds - Array of person IDs to process
 * @param {Array} params.personsInThisRecord - Array of persons in the current record
 * @param {Object} params.defaultValues - Object containing selection states
 * @param {boolean} params.value - Value to set for selections
 */
const setSelectionValues = ({ personIds, personsInThisRecord, defaultValues, value }) => {
  personIds.forEach((personId) => {
    if (personsInThisRecord.some((p) => p.id === personId)) {
      defaultValues.selectedInRecord[personId] = value
    }
    defaultValues.selectedInImage[personId] = value
  })
}

/**
 * Get the default values for the checkboxes. The values are group in selectedInRecord, selectedInImage, and selectedInOtherArks groups.
 * @param {Array} personIdsSharingElement - Array of person ids sharing the element
 * @param {Array} personsInThisRecord - Persons in the current record
 * @param {Array} personsInThisImage - Persons in the current image
 * @param {Object} allTheArksExceptCurrent - person data for other arks section. Expects the ark entries to have a selected group.
 * @returns {{selectedInRecord: *[], selectedInImage: *[], selectedInOtherArks: *[]}}
 */
export const getPrefixedFormDefaultValues = ({
  personIdsSharingElement,
  personsInThisRecord,
  personsInThisImage,
  allTheArksExceptCurrent,
}) => {
  const defaultValues = { selectedInRecord: [], selectedInImage: [], selectedInOtherArks: [] }
  const idsInThisRecord = personsInThisRecord.map((p) => p.id)
  const idsInThisImage = personsInThisImage.map((p) => p.id)

  // Set initial false values for all persons
  setSelectionValues({
    personIds: [...idsInThisRecord, ...idsInThisImage],
    personsInThisRecord,
    defaultValues,
    value: false,
  })

  // Set true values for persons sharing the element
  setSelectionValues({
    personIds: personIdsSharingElement,
    personsInThisRecord,
    defaultValues,
    value: true,
  })

  // Handle other arks
  Object.values(allTheArksExceptCurrent).forEach((obj) => {
    obj.selected.forEach((person) => {
      if (personsInThisRecord.some((p) => p.id === person.id)) {
        defaultValues.selectedInRecord[person.id] = true
      }
      defaultValues.selectedInOtherArks[person.id] = true
    })
  })

  return defaultValues
}
